import { lazy, Suspense, type ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { Shell } from "./components/Shell";
import { HomePage } from "./pages/Home";
import { MarketsPage } from "./pages/Markets";
import { MarketPage } from "./pages/Market";
import { TopicPage } from "./pages/Topic";
import { PortfolioPage } from "./pages/Portfolio";
import { LoginPage, RegisterPage, HowItWorks } from "./pages/Auth";
import { LearnPage, LearnArticle, LegalPage, TrustPage, StatusPage, LeaderboardPage, SuggestPage, HelpPage } from "./pages/Learn";
import { AdminLayout, AdminOverview, AdminMarkets, AdminUsers, AdminFinance, AdminRisk } from "./pages/Admin";
import { AccountPage, ActivityPage, WatchlistPage } from "./pages/Account";
import { ProfilePage } from "./pages/Profile";
import { Onboarding } from "./pages/Onboarding";
import { useAuth } from "./store/auth";

const WalletPage = lazy(() => import("./pages/Wallet"));

function AdminGate({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="wrap">Loading…</div>;
  if (!user || (user.role !== "ADMIN" && user.role !== "SUPER_ADMIN")) return <Navigate to="/login" replace />;
  return children;
}

export default function App() {
  return (
    <>
      <Onboarding />
      <Suspense fallback={<div className="wrap">Loading…</div>}>
        <Routes>
        <Route element={<Shell />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/markets" element={<MarketsPage />} />
          <Route path="/topics/:slug" element={<TopicPage />} />
          <Route path="/markets/:slug" element={<MarketPage />} />
          <Route path="/portfolio" element={<PortfolioPage />} />
          <Route path="/wallet" element={<WalletPage />} />
          <Route path="/watchlist" element={<WatchlistPage />} />
          <Route path="/activity" element={<ActivityPage />} />
          <Route path="/account" element={<AccountPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/learn" element={<LearnPage />} />
          <Route path="/learn/:slug" element={<LearnArticle />} />
          <Route path="/leaderboard" element={<LeaderboardPage />} />
          <Route path="/u/:username" element={<ProfilePage />} />
          <Route path="/suggest" element={<SuggestPage />} />
          <Route path="/help" element={<HelpPage />} />
          <Route path="/trust" element={<TrustPage />} />
          <Route path="/status" element={<StatusPage />} />
          <Route path="/terms" element={<LegalPage slug="terms" />} />
          <Route path="/privacy" element={<LegalPage slug="privacy" />} />
          <Route path="/risk" element={<LegalPage slug="risk" />} />
          <Route path="/resolution-rules" element={<LegalPage slug="resolution-rules" />} />
          <Route path="/fees" element={<LegalPage slug="fees" />} />
          <Route path="/responsible-trading" element={<LegalPage slug="responsible-trading" />} />
          <Route path="/kyc" element={<LegalPage slug="kyc" />} />
          <Route path="/jurisdictions" element={<LegalPage slug="jurisdictions" />} />
          <Route path="/disputes" element={<LegalPage slug="disputes" />} />
          <Route path="/user-agreement" element={<LegalPage slug="user-agreement" />} />
          <Route path="/admin" element={<AdminGate><AdminLayout /></AdminGate>}>
            <Route index element={<AdminOverview />} />
            <Route path="markets" element={<AdminMarkets />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="finance" element={<AdminFinance />} />
            <Route path="risk" element={<AdminRisk />} />
          </Route>
        </Route>
      </Routes>
      </Suspense>
    </>
  );
}
